import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Label2 } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Cube, PuzzlePiece, Plus, PencilSimple, Trash, Stack, SlidersHorizontal, ListBullets, CaretUp, CaretDown, X, Image } from "@phosphor-icons/react";
import { AuthImage } from "@/components/AuthImage";
import { toast } from "sonner";

const groupBy = (arr, key) => arr.reduce((m, x) => { const k = x[key] || "Ohne Kategorie"; (m[k] = m[k] || []).push(x); return m; }, {});

function TypeDialog({ open, onOpenChange, kind, initial, componentKinds, categories, onSaved }) {
  const isAsset = kind === "asset";
  const [form, setForm] = useState({});
  useEffect(() => {
    setForm(initial || { name: "", kategorie: "", beschreibung: "", default_interval_days: "", default_component_kinds: [] });
  }, [initial, open]);

  const save = async () => {
    if (!form.name) return;
    const base = isAsset
      ? `/v1/catalogs/asset-types`
      : `/v1/catalogs/component-types`;
    const payload = isAsset
      ? { name: form.name, kategorie: form.kategorie || null, beschreibung: form.beschreibung || null,
          default_interval_days: form.default_interval_days ? Number(form.default_interval_days) : null,
          default_component_kinds: form.default_component_kinds || [] }
      : { name: form.name, kategorie: form.kategorie || null, art_key: form.art_key || null };
    try {
      if (initial?.id) await api.put(`${base}/${initial.id}`, payload);
      else await api.post(base, payload);
      toast.success(initial?.id ? "Aktualisiert" : "Angelegt");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleComp = (k) => {
    const set = new Set(form.default_component_kinds || []);
    set.has(k) ? set.delete(k) : set.add(k);
    setForm({ ...form, default_component_kinds: [...set] });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="type-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{initial?.id ? "Bearbeiten" : (isAsset ? "Neuer Anlagentyp" : "Neuer Komponententyp")}</DialogTitle></DialogHeader>
        <DialogDescription>{isAsset ? "Anlagentyp inkl. Standard-Komponenten & Wartungsintervall." : "Komponententyp als zentrale Stammdaten."}</DialogDescription>
        <div className="space-y-3">
          <div><Label2>Name</Label2><Input data-testid="type-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="z. B. Sektionaltor" /></div>
          <div><Label2>Kategorie</Label2><Input data-testid="type-kategorie" value={form.kategorie || ""} onChange={(e) => setForm({ ...form, kategorie: e.target.value })} placeholder="z. B. Tore" /></div>
          {!isAsset && (
            <div><Label2>Komponentenart</Label2>
              <Select value={form.art_key || ""} onValueChange={(v) => setForm({ ...form, art_key: v })}>
                <SelectTrigger data-testid="type-art"><SelectValue placeholder="Art wählen" /></SelectTrigger>
                <SelectContent>{(categories || []).map((c) => <SelectItem key={c.key} value={c.key}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {isAsset && <>
            <div><Label2>Standard-Wartungsintervall (Tage)</Label2><Input type="number" value={form.default_interval_days || ""} onChange={(e) => setForm({ ...form, default_interval_days: e.target.value })} placeholder="365" /></div>
            <div>
              <Label2>Standard-Komponenten</Label2>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {componentKinds.map((c) => {
                  const on = (form.default_component_kinds || []).includes(c.key);
                  return <button key={c.key} type="button" onClick={() => toggleComp(c.key)}
                    className={`rounded-md border px-2 py-1 text-xs transition-colors ${on ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-accent"}`}>{c.name}</button>;
                })}
              </div>
            </div>
          </>}
        </div>
        <DialogFooter><Button data-testid="type-save" onClick={save} disabled={!form.name}>Speichern</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CategoryDialog({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState({});
  useEffect(() => { setForm(initial || { name: "", nummer_label: "Komponentennummer", sort: 0 }); }, [initial, open]);
  const save = async () => {
    if (!form.name) return;
    const payload = { name: form.name, nummer_label: form.nummer_label || "Komponentennummer", sort: Number(form.sort) || 0 };
    try {
      if (initial?.id) await api.put(`/v1/catalogs/component-categories/${initial.id}`, payload);
      else await api.post(`/v1/catalogs/component-categories`, payload);
      toast.success(initial?.id ? "Aktualisiert" : "Angelegt"); onOpenChange(false); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="category-dialog">
        <DialogHeader><DialogTitle className="font-heading">{initial?.id ? "Komponentenart bearbeiten" : "Neue Komponentenart"}</DialogTitle></DialogHeader>
        <DialogDescription>Art (z. B. Tor, Tür, Zufahrtskontrolle) – erweiterbar ohne Code.</DialogDescription>
        <div className="space-y-3">
          <div><Label2>Name</Label2><Input data-testid="category-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="z. B. Tor" /></div>
          <div><Label2>Nummern-Bezeichnung (UI-Label)</Label2><Input data-testid="category-numlabel" value={form.nummer_label || ""} onChange={(e) => setForm({ ...form, nummer_label: e.target.value })} placeholder="z. B. Tornummer" /></div>
        </div>
        <DialogFooter><Button data-testid="category-save" onClick={save} disabled={!form.name}>Speichern</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldDefDialog({ open, onOpenChange, initial, categories, componentTypes = [], optionLists = [], onSaved }) {
  const [form, setForm] = useState({});
  useEffect(() => {
    setForm(initial ? { ...initial, options: (initial.options || []).join(", "), option_list_key: initial.option_list_key || "", allow_sonstiges: !!initial.allow_sonstiges }
      : { art_key: categories[0]?.key || "_all", typ_key: "", name: "", field_type: "text", options: "", unit: "", gruppe: "Technische Daten", required: false, sort: 0, option_list_key: "", allow_sonstiges: false });
  }, [initial, open, categories]);
  const save = async () => {
    if (!form.name || !form.art_key) return;
    const payload = { art_key: form.art_key, typ_key: form.typ_key || null, name: form.name, field_type: form.field_type,
      unit: form.unit || null, gruppe: form.gruppe || null, required: !!form.required, sort: Number(form.sort) || 0,
      option_list_key: form.option_list_key || null, allow_sonstiges: !!form.allow_sonstiges,
      options: (form.options || "").split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      if (initial?.id) await api.put(`/v1/catalogs/component-field-defs/${initial.id}`, payload);
      else await api.post(`/v1/catalogs/component-field-defs`, payload);
      toast.success(initial?.id ? "Aktualisiert" : "Angelegt"); onOpenChange(false); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const typesForArt = componentTypes.filter((t) => t.art_key === form.art_key);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="fielddef-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{initial?.id ? "Technisches Feld bearbeiten" : "Neues technisches Feld"}</DialogTitle></DialogHeader>
        <DialogDescription>Ebene wählen: Art (gilt für alle Typen der Art) oder ein bestimmter Typ. Erweiterbar ohne Code.</DialogDescription>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div><Label2>Komponentenart</Label2>
              <Select value={form.art_key} onValueChange={(v) => setForm({ ...form, art_key: v, typ_key: "" })}>
                <SelectTrigger data-testid="fielddef-art"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="_all">Alle Arten</SelectItem>{categories.map((c) => <SelectItem key={c.key} value={c.key}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label2>Nur Typ (optional)</Label2>
              <Select value={form.typ_key || "__all__"} onValueChange={(v) => setForm({ ...form, typ_key: v === "__all__" ? "" : v })}>
                <SelectTrigger data-testid="fielddef-typ"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="__all__">Alle Typen der Art</SelectItem>{typesForArt.map((t) => <SelectItem key={t.key} value={t.key}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label2>Feldname</Label2><Input data-testid="fielddef-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="z. B. Federart" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label2>Feldtyp</Label2>
              <Select value={form.field_type} onValueChange={(v) => setForm({ ...form, field_type: v })}>
                <SelectTrigger data-testid="fielddef-type"><SelectValue /></SelectTrigger>
                <SelectContent>{["text", "number", "date", "bool", "select", "multiselect", "doc_upload", "image_upload"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label2>Einheit</Label2><Input value={form.unit || ""} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="mm, kg, s …" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2 items-end">
            <div><Label2>Gruppe / Bereich</Label2><Input value={form.gruppe || ""} onChange={(e) => setForm({ ...form, gruppe: e.target.value })} placeholder="z. B. Abmessungen" /></div>
            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2"><span className="text-sm">Pflichtfeld</span><Switch data-testid="fielddef-required" checked={!!form.required} onCheckedChange={(v) => setForm({ ...form, required: v })} /></div>
          </div>
          {(form.field_type === "select" || form.field_type === "multiselect") && (
            <>
              <div><Label2>Auswahlliste (empfohlen)</Label2>
                <Select value={form.option_list_key || "__none__"} onValueChange={(v) => setForm({ ...form, option_list_key: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="fielddef-optionlist"><SelectValue placeholder="Keine (freie Optionen)" /></SelectTrigger>
                  <SelectContent><SelectItem value="__none__">Keine (freie Optionen)</SelectItem>{(optionLists || []).map((l) => <SelectItem key={l.key} value={l.key}>{l.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {!form.option_list_key && (
                <div><Label2>Optionen (Komma-getrennt)</Label2><Input data-testid="fielddef-options" value={form.options || ""} onChange={(e) => setForm({ ...form, options: e.target.value })} placeholder="T30, T60, T90" /></div>
              )}
              <div className="flex items-center justify-between rounded-md border border-border px-3 py-2"><span className="text-sm">„Sonstiges" (Freitext) erlauben</span><Switch data-testid="fielddef-sonstiges" checked={!!form.allow_sonstiges} onCheckedChange={(v) => setForm({ ...form, allow_sonstiges: v })} /></div>
            </>
          )}
        </div>
        <DialogFooter><Button data-testid="fielddef-save" onClick={save} disabled={!form.name}>Speichern</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OptionListDialog({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState({ name: "", beschreibung: "", allow_sonstiges: true, items: [] });
  useEffect(() => {
    setForm(initial
      ? { name: initial.name, beschreibung: initial.beschreibung || "", allow_sonstiges: !!initial.allow_sonstiges, items: (initial.items || []).map((i) => ({ ...i, synonyme: (i.synonyme || []).join(", ") })) }
      : { name: "", beschreibung: "", allow_sonstiges: true, items: [{ label: "", gruppe: "", synonyme: "" }] });
  }, [initial, open]);
  const setItem = (idx, patch) => setForm((f) => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { label: "", gruppe: "", synonyme: "" }] }));
  const rmItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const move = (idx, dir) => setForm((f) => { const a = [...f.items]; const j = idx + dir; if (j < 0 || j >= a.length) return f; [a[idx], a[j]] = [a[j], a[idx]]; return { ...f, items: a }; });
  const save = async () => {
    if (!form.name) return;
    const items = form.items.filter((it) => (it.label || "").trim()).map((it, i) => ({
      key: it.key || undefined, label: it.label.trim(), gruppe: (it.gruppe || "").trim() || null,
      synonyme: (it.synonyme || "").split(",").map((s) => s.trim()).filter(Boolean), sort: i, active: it.active !== false }));
    const payload = { name: form.name, beschreibung: form.beschreibung || null, allow_sonstiges: !!form.allow_sonstiges, items };
    try {
      if (initial?.id) await api.put(`/v1/catalogs/component-option-lists/${initial.id}`, payload);
      else await api.post(`/v1/catalogs/component-option-lists`, payload);
      toast.success(initial?.id ? "Aktualisiert" : "Angelegt"); onOpenChange(false); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="optionlist-dialog" className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader><DialogTitle className="font-heading">{initial?.id ? "Auswahlliste bearbeiten" : "Neue Auswahlliste"}</DialogTitle></DialogHeader>
        <DialogDescription>Wiederverwendbare Werteliste (mit Gruppen & Synonymen für Suche/OCR). Wird in technischen Feldern verknüpft.</DialogDescription>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div><Label2>Name</Label2><Input data-testid="optionlist-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="z. B. Türblattmaterial" /></div>
            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2 self-end"><span className="text-sm">„Sonstiges" erlauben</span><Switch data-testid="optionlist-sonstiges" checked={!!form.allow_sonstiges} onCheckedChange={(v) => setForm({ ...form, allow_sonstiges: v })} /></div>
          </div>
          <div className="flex items-center justify-between"><Label2>Einträge</Label2><Button size="sm" variant="outline" className="gap-1.5" data-testid="optionlist-add-item" onClick={addItem}><Plus size={13} weight="bold" /> Eintrag</Button></div>
          <div className="space-y-2">
            {form.items.map((it, idx) => (
              <div key={idx} data-testid={`optionlist-item-${idx}`} className="rounded-md border border-border p-2">
                <div className="flex items-center gap-1.5">
                  <Input className="flex-1" value={it.label || ""} placeholder="Wert (z. B. Edelstahl)" onChange={(e) => setItem(idx, { label: e.target.value })} data-testid={`optionlist-item-label-${idx}`} />
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => move(idx, -1)}><CaretUp size={14} /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => move(idx, 1)}><CaretDown size={14} /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => rmItem(idx)} data-testid={`optionlist-item-remove-${idx}`}><X size={14} /></Button>
                </div>
                <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                  <Input value={it.gruppe || ""} placeholder="Gruppe (optional)" onChange={(e) => setItem(idx, { gruppe: e.target.value })} />
                  <Input value={it.synonyme || ""} placeholder="Synonyme, Komma-getrennt" onChange={(e) => setItem(idx, { synonyme: e.target.value })} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter><Button data-testid="optionlist-save" onClick={save} disabled={!form.name}>Speichern</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BrandingPanel() {
  const [logo, setLogo] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/v1/branding").then((r) => setLogo(r.data.logo_media_id)).catch(() => setLogo(null)); }, []);
  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", files[0]);
      const { data } = await api.post("/v1/branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogo(data.logo_media_id); toast.success("Logo gespeichert");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const remove = async () => { try { await api.delete("/v1/branding/logo"); setLogo(null); toast.success("Logo entfernt"); } catch (e) { toast.error("Fehler beim Entfernen"); } };
  return (
    <div className="max-w-md space-y-3" data-testid="branding-panel">
      <Label2>Firmenlogo für QR-Etiketten</Label2>
      <p className="text-xs text-muted-foreground">Wird auf den QR-Etiketten anstelle des „C"-Badges gedruckt (empfohlen: PNG mit transparentem Hintergrund).</p>
      <div className="flex items-center gap-3 rounded-md border border-border p-3">
        <div className="flex h-16 w-32 items-center justify-center rounded-md bg-muted">
          {logo ? <AuthImage mediaId={logo} alt="Logo" className="max-h-16 max-w-full object-contain" dataTestid="branding-logo-preview" /> : <span className="text-xs text-muted-foreground">Kein Logo</span>}
        </div>
        <div className="flex flex-col gap-2">
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-accent" data-testid="branding-upload-label">
            <Image size={15} /> {busy ? "…" : "Logo hochladen"}
            <input type="file" accept="image/*" className="hidden" data-testid="branding-file-input" onChange={(e) => upload(e.target.files)} />
          </label>
          {logo && <Button variant="ghost" size="sm" className="text-red-600" data-testid="branding-remove" onClick={remove}>Entfernen</Button>}
        </div>
      </div>
    </div>
  );
}

export default function Stammdaten() {
  const [assetTypes, setAssetTypes] = useState(null);
  const [compTypes, setCompTypes] = useState(null);
  const [categories, setCategories] = useState(null);
  const [fieldDefs, setFieldDefs] = useState(null);
  const [optionLists, setOptionLists] = useState([]);
  const [dlg, setDlg] = useState({ open: false, kind: "asset", initial: null });
  const [catDlg, setCatDlg] = useState({ open: false, initial: null });
  const [fieldDlg, setFieldDlg] = useState({ open: false, initial: null });
  const [optDlg, setOptDlg] = useState({ open: false, initial: null });

  const load = useCallback(() => {
    api.get("/v1/catalogs/asset-types").then((r) => setAssetTypes(r.data));
    api.get("/v1/catalogs/component-types").then((r) => setCompTypes(r.data));
    api.get("/v1/catalogs/component-categories").then((r) => setCategories(r.data)).catch(() => setCategories([]));
    api.get("/v1/catalogs/component-field-defs").then((r) => setFieldDefs(r.data)).catch(() => setFieldDefs([]));
    api.get("/v1/catalogs/component-option-lists").then((r) => setOptionLists(r.data)).catch(() => setOptionLists([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const deactivate = async (kind, id) => {
    const map = { asset: "/v1/catalogs/asset-types", component: "/v1/catalogs/component-types",
      category: "/v1/catalogs/component-categories", fielddef: "/v1/catalogs/component-field-defs",
      optionlist: "/v1/catalogs/component-option-lists" };
    try { await api.delete(`${map[kind]}/${id}`); toast.success("Deaktiviert"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!assetTypes || !compTypes || !categories || !fieldDefs) return <div className="space-y-4"><Skeleton className="h-24 rounded-md" /><Skeleton className="h-64 rounded-md" /></div>;
  const catName = (k) => k === "_all" ? "Alle Arten" : (categories.find((c) => c.key === k)?.name || k);

  const renderList = (items, kind) => {
    const groups = groupBy(items, "kategorie");
    return Object.entries(groups).map(([kat, list]) => (
      <div key={kat} className="mb-5">
        <div className="mb-2 flex items-center gap-2"><Label2>{kat}</Label2><span className="text-xs text-muted-foreground">({list.length})</span></div>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((t) => (
            <Card key={t.id} data-testid={`${kind}-type-${t.key}`} className="flex items-center justify-between gap-2 rounded-md border p-3">
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold">{t.name}</div>
                {kind === "asset" && <div className="mt-0.5 text-xs text-muted-foreground">
                  {(t.default_component_kinds || []).length} Std.-Komponenten{t.default_interval_days ? ` · ${t.default_interval_days} T` : ""}
                </div>}
              </div>
              <div className="flex shrink-0 gap-1">
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setDlg({ open: true, kind, initial: t })} data-testid={`${kind}-edit-${t.key}`}><PencilSimple size={14} /></Button>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => deactivate(kind, t.id)}><Trash size={14} /></Button>
              </div>
            </Card>
          ))}
        </div>
      </div>
    ));
  };

  return (
    <div>
      <PageHeader title="Stammdaten · Kataloge" subtitle="Zentrale, erweiterbare Anlagen- & Komponententypen · Grundlage der Fachlogik" />
      <Tabs defaultValue="asset">
        <TabsList data-testid="stammdaten-tabs">
          <TabsTrigger value="asset" data-testid="tab-asset-types"><Cube size={15} weight="duotone" className="mr-1.5" /> Anlagentypen ({assetTypes.length})</TabsTrigger>
          <TabsTrigger value="component" data-testid="tab-component-types"><PuzzlePiece size={15} weight="duotone" className="mr-1.5" /> Komponententypen ({compTypes.length})</TabsTrigger>
          <TabsTrigger value="category" data-testid="tab-component-categories"><Stack size={15} weight="duotone" className="mr-1.5" /> Komponentenarten ({categories.length})</TabsTrigger>
          <TabsTrigger value="fielddef" data-testid="tab-field-defs"><SlidersHorizontal size={15} weight="duotone" className="mr-1.5" /> Technische Felder ({fieldDefs.length})</TabsTrigger>
          <TabsTrigger value="optionlist" data-testid="tab-option-lists"><ListBullets size={15} weight="duotone" className="mr-1.5" /> Auswahllisten ({optionLists.length})</TabsTrigger>
          <TabsTrigger value="branding" data-testid="tab-branding"><Image size={15} weight="duotone" className="mr-1.5" /> Branding</TabsTrigger>
        </TabsList>
        <TabsContent value="asset" className="mt-4">
          <div className="mb-4"><Button size="sm" className="gap-2" data-testid="add-asset-type-btn" onClick={() => setDlg({ open: true, kind: "asset", initial: null })}><Plus size={15} weight="bold" /> Anlagentyp</Button></div>
          {renderList(assetTypes, "asset")}
        </TabsContent>
        <TabsContent value="component" className="mt-4">
          <div className="mb-4"><Button size="sm" className="gap-2" data-testid="add-component-type-btn" onClick={() => setDlg({ open: true, kind: "component", initial: null })}><Plus size={15} weight="bold" /> Komponententyp</Button></div>
          {renderList(compTypes, "component")}
        </TabsContent>
        <TabsContent value="category" className="mt-4">
          <div className="mb-4"><Button size="sm" className="gap-2" data-testid="add-category-btn" onClick={() => setCatDlg({ open: true, initial: null })}><Plus size={15} weight="bold" /> Komponentenart</Button></div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {categories.map((c) => (
              <Card key={c.id} data-testid={`category-${c.key}`} className="flex items-center justify-between gap-2 rounded-md border p-3">
                <div className="min-w-0"><div className="truncate text-sm font-semibold">{c.name}</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">Nummer-Label: {c.nummer_label}</div></div>
                <div className="flex shrink-0 gap-1">
                  <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`category-edit-${c.key}`} onClick={() => setCatDlg({ open: true, initial: c })}><PencilSimple size={14} /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => deactivate("category", c.id)}><Trash size={14} /></Button>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="fielddef" className="mt-4">
          <div className="mb-4"><Button size="sm" className="gap-2" data-testid="add-field-def-btn" onClick={() => setFieldDlg({ open: true, initial: null })}><Plus size={15} weight="bold" /> Technisches Feld</Button></div>
          {Object.entries(groupBy(fieldDefs, "art_key")).map(([art, list]) => (
            <div key={art} className="mb-5">
              <div className="mb-2 flex items-center gap-2"><Label2>{catName(art)}</Label2><span className="text-xs text-muted-foreground">({list.length})</span></div>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {list.map((f) => (
                  <Card key={f.id} data-testid={`fielddef-${f.art_key}-${f.key}`} className="flex items-center justify-between gap-2 rounded-md border p-3">
                    <div className="min-w-0"><div className="truncate text-sm font-semibold">{f.name}</div>
                      <div className="mt-0.5 text-xs text-muted-foreground">{f.field_type}{f.required ? " · Pflicht" : ""}{f.typ_key ? ` · nur ${f.typ_key}` : " · Art-Ebene"}{f.unit ? ` · ${f.unit}` : ""}{f.gruppe ? ` · ${f.gruppe}` : ""}</div></div>
                    <div className="flex shrink-0 gap-1">
                      <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`fielddef-edit-${f.key}`} onClick={() => setFieldDlg({ open: true, initial: f })}><PencilSimple size={14} /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => deactivate("fielddef", f.id)}><Trash size={14} /></Button>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </TabsContent>
        <TabsContent value="optionlist" className="mt-4">
          <div className="mb-4"><Button size="sm" className="gap-2" data-testid="add-option-list-btn" onClick={() => setOptDlg({ open: true, initial: null })}><Plus size={15} weight="bold" /> Auswahlliste</Button></div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {optionLists.map((l) => (
              <Card key={l.id} data-testid={`optionlist-${l.key}`} className="flex items-start justify-between gap-2 rounded-md border p-3">
                <div className="min-w-0"><div className="truncate text-sm font-semibold">{l.name}</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">{(l.items || []).length} Einträge{l.allow_sonstiges ? " · Sonstiges erlaubt" : ""}</div>
                  {(() => { const uses = (fieldDefs || []).filter((f) => f.option_list_key === l.key); return uses.length
                    ? <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`optionlist-usage-${l.key}`}>{uses.map((f) => <Badge key={f.id} variant="outline" className="rounded-md text-[10px]">{f.typ_key || f.art_key} · {f.name}</Badge>)}</div>
                    : <div className="mt-1.5 text-[11px] italic text-muted-foreground" data-testid={`optionlist-usage-${l.key}`}>Noch keinem Feld zugeordnet</div>; })()}
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`optionlist-edit-${l.key}`} onClick={() => setOptDlg({ open: true, initial: l })}><PencilSimple size={14} /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => deactivate("optionlist", l.id)}><Trash size={14} /></Button>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="branding" className="mt-4">
          <BrandingPanel />
        </TabsContent>
      </Tabs>
      <TypeDialog open={dlg.open} onOpenChange={(o) => setDlg({ ...dlg, open: o })} kind={dlg.kind}
        initial={dlg.initial} componentKinds={compTypes} categories={categories} onSaved={load} />
      <CategoryDialog open={catDlg.open} onOpenChange={(o) => setCatDlg({ ...catDlg, open: o })} initial={catDlg.initial} onSaved={load} />
      <FieldDefDialog open={fieldDlg.open} onOpenChange={(o) => setFieldDlg({ ...fieldDlg, open: o })} initial={fieldDlg.initial} categories={categories} componentTypes={compTypes} optionLists={optionLists} onSaved={load} />
      <OptionListDialog open={optDlg.open} onOpenChange={(o) => setOptDlg({ ...optDlg, open: o })} initial={optDlg.initial} onSaved={load} />
    </div>
  );
}
